import { useState } from 'react'
import * as S from './PromoCodeForm.styles'
import './PromoCodeForm.scss'
import { promoCodeManager } from '../../../services/PromoCodeManager'
import {
  DISCOUNT_TYPES,
  PROMO_CODE_ERRORS_MESSAGES,
} from '../../../stores/promoCodeStore/promoCode.consts'
import DummySubmit from '../../DummySubmit/DummySubmit'
import { translateManager as t } from '../../../services/TranslateManager'
import { logger } from '../../../services/logger'
import { withApi } from '../../../api/apiProvider'
import TextInput from 'mixtiles-web-common/ui/TextInput'
import AlertPopup from '../../AlertPopup/AlertPopup'
import GiftCardSuccessDialog from '../GiftCardSuccessDialog/GiftCardSuccessDialog'
import { formatPrice } from '../../../utils/utils'
import { isBookGiftCard } from '../../../stores/promoCodeStore/promoCode.utils'

type PromoCodeSuccessInfo = {
  discountType?: string
  amount?: number
  code?: string
  metadata?: {
    remainingValue?: number
    currency?: string
    isGiftCard?: boolean
    tileSizesList?: string[]
  }
}

type Props = {
  onDismiss: () => void
  onSuccess?: (info: PromoCodeSuccessInfo) => void
  onClose: () => void
  source?: string
  api?: any // Replace with proper API type
}

function PromoCodeForm({ onDismiss, onSuccess, onClose, source }: Props) {
  const [promoCode, setPromoCode] = useState<string>('')
  const [showPromoFailureDialog, setShowPromoFailureDialog] = useState(false)
  const [promoFailureDialogText, setPromoFailureDialogText] = useState('')
  const [indicateInvalidInput, setIndicateInvalidInput] = useState(false)
  const [promoCodeSuccessInfo, setPromoCodeSuccessInfo] =
    useState<PromoCodeSuccessInfo>({})
  const [showPromoCodeSuccessDialog, setShowPromoCodeSuccessDialog] =
    useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handlePromoCodeInputChanged = (value: string) => {
    const MAX_LENGTH = 30
    if (value.length <= MAX_LENGTH) {
      setPromoCode(value.toUpperCase())
    }
  }

  const getGiftCardSubtitleText = (
    successInfo: PromoCodeSuccessInfo
  ): string => {
    if (successInfo.discountType === DISCOUNT_TYPES.GIFT_CARD) {
      return formatPrice(
        successInfo?.metadata?.remainingValue,
        successInfo?.metadata?.currency
      )
    }

    const amount = successInfo.amount ?? 0
    const tileSizesList = successInfo.metadata?.tileSizesList
    const isBook = isBookGiftCard(tileSizesList)

    if (isBook) {
      return amount > 1
        ? t.get(
            'general.promo_code_success_dialog.subtitle.gift_card_books_many',
            { v1: amount }
          )
        : t.get(
            'general.promo_code_success_dialog.subtitle.gift_card_books_one'
          )
    } else {
      return amount > 1
        ? t.get(
            'general.promo_code_success_dialog.subtitle.gift_card_tiles_many',
            { v1: amount }
          )
        : t.get(
            'general.promo_code_success_dialog.subtitle.gift_card_tiles_one'
          )
    }
  }

  const handleDone = async () => {
    if (!(promoCode && promoCode.toUpperCase().trim())) {
      setIndicateInvalidInput(true)
      return
    }
    setSubmitting(true)
    try {
      const { discountType, amount, error, code, metadata, category } =
        await promoCodeManager.addPromoCode({
          code: promoCode,
          source,
        })
      setSubmitting(false)

      if (error) {
        handlePromoFailure(error)
      } else {
        const isGiftCard =
          discountType === DISCOUNT_TYPES.GIFT_CARD ||
          (discountType === DISCOUNT_TYPES.UNIT && category === 'Gift Cards')

        if (isGiftCard) {
          setShowPromoCodeSuccessDialog(true)
          setPromoCodeSuccessInfo({
            discountType,
            amount,
            metadata,
          })
        } else {
          setPromoCodeSuccessInfo({
            discountType,
            amount,
            code,
          })
          onClose()
        }
      }
    } catch (error) {
      logger.error('Failed to add promo-code', error)
      handlePromoFailure(error as string)
    }
  }

  const handlePromoFailure = (reason: string) => {
    const key =
      reason in PROMO_CODE_ERRORS_MESSAGES
        ? PROMO_CODE_ERRORS_MESSAGES[
            reason as keyof typeof PROMO_CODE_ERRORS_MESSAGES
          ]
        : PROMO_CODE_ERRORS_MESSAGES.DEFAULT

    setPromoFailureDialogText(t.get(key))
    setShowPromoFailureDialog(true)
  }

  const handleCloseAllPromoDialogs = () => {
    if (showPromoCodeSuccessDialog) {
      onSuccess?.(promoCodeSuccessInfo)
      onDismiss()
    }
    setShowPromoCodeSuccessDialog(false)
    setShowPromoFailureDialog(false)
    setPromoCodeSuccessInfo({})
  }

  /* Handle form submit action (pressing Enter on the final input */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    await handleDone()
  }

  return (
    <>
      <S.Container className="PromoCodeForm">
        <S.TopBar>
          <S.CloseButton onClick={onClose} />
          <S.Title>{t.get('general.menu.add_promo_code.modal.title')}</S.Title>
          <S.DoneButton
            loading={submitting}
            onClick={handleDone}
            testId="promo-code-done-button"
          >
            {t.get('general.add')}
          </S.DoneButton>
        </S.TopBar>
        <div className="content">
          <form onSubmit={handleSubmit}>
            <S.Label>
              {t.get('general.menu.add_promo_code.modal.form_title')}
            </S.Label>
            <TextInput
              variant="white"
              fullWidth
              placeholder={t.get(
                'general.menu.add_promo_code.modal.placeholder'
              )}
              value={promoCode?.toUpperCase()}
              autoFocus
              onChange={handlePromoCodeInputChanged}
              isInvalid={indicateInvalidInput}
              testId="promo-code-input"
            />
            <DummySubmit />
          </form>
        </div>
      </S.Container>
      <AlertPopup
        open={showPromoFailureDialog}
        title={promoFailureDialogText}
        onClick={handleCloseAllPromoDialogs}
        buttonText={t.get('general.ok')}
        titleTestId="promo-code-failure-dialog"
      />
      {(promoCodeSuccessInfo.discountType === DISCOUNT_TYPES.GIFT_CARD ||
        (promoCodeSuccessInfo.discountType === DISCOUNT_TYPES.UNIT &&
          promoCodeSuccessInfo.metadata?.isGiftCard)) && (
        <GiftCardSuccessDialog
          open={showPromoCodeSuccessDialog}
          onClose={handleCloseAllPromoDialogs}
          title={t.get('general.promo_code_success_dialog.title.gift_card')}
          subtitle={t.get(
            'general.promo_code_success_dialog.subtitle.gift_card',
            {
              v1: getGiftCardSubtitleText(promoCodeSuccessInfo),
            }
          )}
          onDismiss={handleCloseAllPromoDialogs}
        />
      )}
    </>
  )
}

export default withApi(PromoCodeForm)
